import React from "react";
import { Link } from "react-router-dom";

const OPERATOR = "HQPhD Global Ventures LLC";
const UPDATED = "October 2026";

const Page = ({ eyebrow, title, intro, children, testId }) => (
  <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16" data-testid={testId}>
    <div className="text-xs uppercase tracking-[0.28em] text-[#C93B3B]">{eyebrow}</div>
    <h1 className="font-serif text-5xl lg:text-6xl mt-4 text-[#F7ECEB]">{title}</h1>
    {intro && <p className="text-[#A89896] mt-6 leading-relaxed">{intro}</p>}
    <div className="mt-10 space-y-8 text-[#A89896] leading-relaxed">{children}</div>
  </div>
);

const Section = ({ title, children }) => (
  <section>
    <h2 className="font-serif text-2xl text-[#F7ECEB] mb-3">{title}</h2>
    <div className="space-y-3 text-sm sm:text-base">{children}</div>
  </section>
);

const Draft = () => (
  <p className="text-xs uppercase tracking-[0.2em] text-[#E58B5E]">Draft for legal review · Last updated {UPDATED}</p>
);

export function About() {
  return (
    <Page eyebrow="About" title="Touch, held with care." testId="about-page"
      intro="Touche.Today is a private membership platform for platonic companionship, therapeutic touch, and intimacy wellness coaching.">
      <Section title="Who we are">
        <p>Touche.Today is owned and operated by <strong className="text-[#F7ECEB]">{OPERATOR}</strong>. The platform was founded by Hilda Quincena, an intimacy wellness coach, to make consent-forward, trauma-informed touch available in a safe and professional setting.</p>
      </Section>
      <Section title="What we do">
        <p>We connect members with vetted, independent cuddle therapists for strictly platonic sessions, and offer one-on-one intimacy and sex wellness coaching with our founder for premium members. Every practitioner is identity-verified and approved by our team before appearing on the platform.</p>
      </Section>
      <Section title="Our commitments">
        <p>Consent is ongoing and may be withdrawn at any time. Sessions are non-sexual. Practitioners are independent professionals who set their own availability and rates. Your information and session details are kept confidential.</p>
      </Section>
      <Section title="Get in touch">
        <p>Questions? Visit our <Link to="/contact" className="text-[#C93B3B] underline">Contact page</Link> or email <a href="mailto:hello@touche.today" className="text-[#C93B3B] underline">hello@touche.today</a>.</p>
      </Section>
    </Page>
  );
}

export function Contact() {
  return (
    <Page eyebrow="Contact" title="We're here." testId="contact-page"
      intro="For membership questions, practitioner applications, safety concerns, or legal and privacy requests, reach us by email.">
      <Section title="Email">
        <p><a href="mailto:hello@touche.today" className="text-[#C93B3B] underline text-lg" data-testid="contact-email">hello@touche.today</a></p>
        <p>We aim to respond within two business days. Safety concerns are prioritized.</p>
      </Section>
      <Section title="Operator">
        <p>Touche.Today is owned and operated by <strong className="text-[#F7ECEB]" data-testid="contact-operator">{OPERATOR}</strong>.</p>
      </Section>
      <Section title="Legal">
        <p>See our <Link to="/terms" className="text-[#C93B3B] underline">Terms of Use</Link> and <Link to="/privacy" className="text-[#C93B3B] underline">Privacy Policy</Link>.</p>
      </Section>
    </Page>
  );
}

export function Terms() {
  return (
    <Page eyebrow="Legal" title="Terms of Use" testId="terms-page"
      intro={`These Terms of Use ("Terms") govern your access to and use of Touche.Today (the "Platform"), which is owned and operated by ${OPERATOR} ("we", "us", or "our"). By creating an account or using the Platform you agree to these Terms.`}>
      <Draft />
      <Section title="1. The Platform">
        <p>Touche.Today is an online marketplace that connects members ("Clients") with independent companionship and therapeutic-touch practitioners ("Practitioners"), and offers coaching services provided by our founder. Except for founder-led coaching, we do not provide sessions ourselves; Practitioners are independent contractors, not our employees or agents.</p>
      </Section>
      <Section title="2. Eligibility and accounts">
        <p>You must be at least 18 years old to use the Platform. You agree to provide accurate information, keep your login credentials secure, and accept responsibility for activity under your account. Practitioners must complete identity verification and be approved by us before offering sessions.</p>
      </Section>
      <Section title="3. Strictly platonic conduct">
        <p>All sessions arranged through the Platform are non-sexual. Any solicitation, request, or offer of sexual activity is prohibited and will result in immediate account termination and, where appropriate, referral to law enforcement. Consent is ongoing; either party may end a session at any time.</p>
      </Section>
      <Section title="4. Bookings, payments, and fees">
        <p>Session prices are set by Practitioners and shown before you book. Payments are processed securely by our payment provider; we do not store full card details. We may charge a client service fee and retain a platform commission from Practitioner earnings, each disclosed within the Platform. Practitioners have 24 hours to accept a session request; requests that are declined or unanswered are refunded in full.</p>
      </Section>
      <Section title="5. Cancellations and refunds">
        <p>Clients may cancel a confirmed session for a full refund up to 24 hours before its start time. Cancellations within 24 hours are non-refundable unless the Practitioner cancels, in which case a full refund is issued. Memberships renew automatically until cancelled and are non-refundable for the current billing period.</p>
      </Section>
      <Section title="6. Practitioner obligations">
        <p>Practitioners agree to maintain professional boundaries, honor accepted bookings, keep their availability accurate, and comply with all applicable laws. Practitioners are responsible for their own taxes and licenses. Earnings are paid out according to the payout process described in the Practitioner Portal.</p>
      </Section>
      <Section title="7. Prohibited uses">
        <p>You may not use the Platform to harass, threaten, or discriminate; to arrange services outside the Platform to avoid fees; to share another user's private information; to post false reviews; or to interfere with the Platform's operation or security.</p>
      </Section>
      <Section title="8. Content and reviews">
        <p>You retain ownership of content you post but grant us a license to display it on the Platform. Reviews must reflect genuine completed sessions. We may remove content that violates these Terms.</p>
      </Section>
      <Section title="9. Disclaimers and limitation of liability">
        <p>The Platform is provided "as is". Sessions are not medical, psychological, or therapeutic treatment and are not a substitute for professional care. To the fullest extent permitted by law, {OPERATOR} is not liable for the conduct of any user or Practitioner, and our total liability for any claim is limited to the amount you paid to us in the twelve months preceding the claim.</p>
      </Section>
      <Section title="10. Termination">
        <p>We may suspend or terminate accounts that violate these Terms or pose a safety risk. You may close your account at any time by contacting us.</p>
      </Section>
      <Section title="11. Changes and governing law">
        <p>We may update these Terms; continued use after changes constitutes acceptance. These Terms are governed by the laws of the United States and the state in which {OPERATOR} is organized, without regard to conflict-of-law principles.</p>
      </Section>
      <Section title="12. Contact">
        <p>Questions about these Terms: <a href="mailto:hello@touche.today" className="text-[#C93B3B] underline">hello@touche.today</a>.</p>
      </Section>
    </Page>
  );
}

export function Privacy() {
  return (
    <Page eyebrow="Legal" title="Privacy Policy" testId="privacy-page"
      intro={`This Privacy Policy explains how ${OPERATOR}, the owner and operator of Touche.Today, collects, uses, and protects your personal information.`}>
      <Draft />
      <Section title="1. Information we collect">
        <p><strong className="text-[#F7ECEB]">Account information:</strong> name, email address, password (stored hashed), and membership tier.</p>
        <p><strong className="text-[#F7ECEB]">Practitioner verification:</strong> government ID and profile photos submitted by applicants, stored in restricted, access-controlled storage and visible only to our administrators.</p>
        <p><strong className="text-[#F7ECEB]">Bookings and messages:</strong> session details, notes you provide, reviews, and messages exchanged through the Platform.</p>
        <p><strong className="text-[#F7ECEB]">Payments:</strong> processed by our payment provider (Stripe). We receive transaction confirmations and the last digits of your card, never your full card number.</p>
        <p><strong className="text-[#F7ECEB]">Technical data:</strong> IP address, browser type, and basic usage logs needed to operate and secure the Platform.</p>
      </Section>
      <Section title="2. How we use information">
        <p>To create and manage your account; match Clients with Practitioners; process bookings, payments, refunds, and payouts; send transactional emails (booking requests, confirmations, reminders, messages); verify Practitioner identity; enforce our Terms; and improve the Platform.</p>
      </Section>
      <Section title="3. How we share information">
        <p>With Practitioners or Clients as needed to fulfil a booking (for example, your first name, session details, and notes); with service providers who host our infrastructure, process payments, and deliver email; and when required by law or to protect the safety of our users. We do not sell personal information.</p>
      </Section>
      <Section title="4. Retention">
        <p>We keep account and booking records while your account is active and as required for legal, tax, and dispute-resolution purposes. Verification documents are retained only as long as needed to confirm eligibility and comply with law.</p>
      </Section>
      <Section title="5. Your choices and rights">
        <p>You may update your profile at any time, cancel memberships from your dashboard, and request access to, correction of, or deletion of your personal information by emailing us. We honor applicable rights under state privacy laws.</p>
      </Section>
      <Section title="6. Security">
        <p>We use encryption in transit, hashed passwords, role-based access controls, and restricted storage for sensitive documents. No system is perfectly secure; please use a strong, unique password.</p>
      </Section>
      <Section title="7. Children">
        <p>The Platform is for adults 18 and older. We do not knowingly collect information from minors.</p>
      </Section>
      <Section title="8. Changes">
        <p>We may update this Policy and will revise the date above when we do. Continued use after changes constitutes acceptance.</p>
      </Section>
      <Section title="9. Contact">
        <p>Privacy questions or requests: <a href="mailto:hello@touche.today" className="text-[#C93B3B] underline">hello@touche.today</a>. Touche.Today is owned and operated by {OPERATOR}.</p>
      </Section>
    </Page>
  );
}
